import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

function buildUserMessage(question) {
  return {
    role: 'user',
    content: question,
    id: Date.now().toString(),
  };
}

function buildAssistantComparisonMessage(responseData) {
  return {
    role: 'assistant',
    content: responseData.responses.length === 1 ? 'Réponse du modèle sélectionné' : 'Réponses comparées',
    comparison: responseData.responses,
    sources: responseData.sources,
    preferred_model: responseData.preferred_model,
    message_type: 'comparison',
    id: responseData.assistant_message_id,
  };
}

function buildAssistantErrorMessage() {
  return {
    role: 'assistant',
    content: "Désolé, une erreur s'est produite. Veuillez réessayer.",
    id: (Date.now() + 1).toString(),
  };
}

function buildComparisonRequestPayload(question, requestConfig) {
  return {
    knowledge_base_id: requestConfig.selectedKB.id,
    query: question,
    conversation_id: requestConfig.conversationId,
    selected_models: requestConfig.selectedModels,
  };
}

function canSubmitMessage(input, requestConfig, loading) {
  return Boolean(input.trim() && requestConfig.selectedKB && !loading && requestConfig.selectedModels.length > 0);
}

function applyComparisonResponse(response, requestConfig) {
  if (response.data.stats) {
    requestConfig.setPreferenceStats(response.data.stats);
  }
  if (!requestConfig.conversationId && response.data.conversation_id) {
    requestConfig.onConversationCreated(response.data.conversation_id);
  }
}

function useConversationHistory(conversationId) {
  const [messages, setMessages] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);

  const loadMessages = useCallback(async (conversationKey) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/conversations/${conversationKey}/messages`);
      setMessages(response.data);
    } catch {
      toast.error('Erreur de chargement des messages');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (conversationId) {
      loadMessages(conversationId);
      return;
    }
    setMessages([]);
  }, [conversationId, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return {
    messages,
    setMessages,
    loadingHistory,
    messagesEndRef,
  };
}

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 300; // 10 minutes safety cap

async function pollCompareJob(jobId, onProgress) {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    const { data } = await axios.get(`${API}/chat/compare-job/${jobId}`);
    if (data.progress && onProgress) {
      onProgress(data.progress);
    }
    if (data.status === 'completed') {
      return data.result;
    }
    if (data.status === 'error') {
      throw new Error(data.error_message || 'Erreur lors de la génération de la réponse');
    }
  }
  throw new Error("La génération a dépassé le délai maximal d'attente");
}

function useChatSubmission({ conversationId, onConversationCreated, selectedKB, selectedModels, setMessages, setPreferenceStats }) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingProgress, setPendingProgress] = useState(null);

  const requestConfig = useMemo(
    () => ({ conversationId, onConversationCreated, selectedKB, selectedModels, setPreferenceStats }),
    [conversationId, onConversationCreated, selectedKB, selectedModels, setPreferenceStats]
  );

  const handleSend = useCallback(async (event) => {
    event.preventDefault();
    if (!canSubmitMessage(input, requestConfig, loading)) {
      return;
    }

    const question = input;
    // Nouvelle question = nouvelle conversation : l'ancienne reste consultable via la sidebar.
    // On vide la fenêtre et on reset `conversationId` pour que le backend en crée une nouvelle.
    const freshRequestConfig = { ...requestConfig, conversationId: null };
    setMessages([buildUserMessage(question)]);
    setInput('');
    setLoading(true);
    setPendingProgress({ current_step: 0, total_steps: 0, label: 'Démarrage...' });

    try {
      const payload = buildComparisonRequestPayload(question, freshRequestConfig);
      const { data: job } = await axios.post(`${API}/chat/compare`, payload);
      const result = await pollCompareJob(job.job_id, setPendingProgress);
      const response = { data: result };
      setMessages((current) => [...current, buildAssistantComparisonMessage(response.data)]);
      applyComparisonResponse(response, freshRequestConfig);
    } catch {
      toast.error('Erreur lors de la génération de la réponse');
      setMessages((current) => [...current, buildAssistantErrorMessage()]);
    } finally {
      setLoading(false);
      setPendingProgress(null);
    }
  }, [input, loading, requestConfig, setMessages]);

  return {
    input,
    setInput,
    loading,
    handleSend,
    pendingProgress,
  };
}

export function useChatMessages({
  conversationId,
  onConversationCreated,
  selectedKB,
  selectedModels,
  setPreferenceStats,
}) {
  const { messages, setMessages, loadingHistory, messagesEndRef } = useConversationHistory(conversationId);
  const { input, setInput, loading, handleSend, pendingProgress } = useChatSubmission({
    conversationId,
    onConversationCreated,
    selectedKB,
    selectedModels,
    setMessages,
    setPreferenceStats,
  });

  return {
    messages,
    setMessages,
    input,
    setInput,
    loading,
    loadingHistory,
    messagesEndRef,
    handleSend,
    pendingProgress,
  };
}
